// Voicemail drop in the phone dock (round 13, "drop and go"): the active call card's Drop voicemail control and
// its picker, and the brief "Dropping <name>" card after verified release. One tap requests
// the line's default; the chevron opens the picker in the contract's order (this number's drops, then mine).
// Accepted means queued. The active call stays until this user's release is verified and local audio closes.
import type { Call } from '@bcio-voice/contracts';
import { type KeyboardEvent, useEffect, useId, useMemo, useReducer, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Star, Voicemail } from 'lucide-react';
import { VoicemailDropsClient, isAbortError, isApiRequestError } from '../../lib/api.ts';
import { formatDuration, formatPhone } from '../../lib/format.ts';
import { Link } from '../../lib/router.tsx';
import type { ActivationGuard } from '../../call/gating.ts';
import { Icon } from '../../ui/Icon.tsx';
import { useAnnouncer } from '../../ui/Announcer.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { type DropOption, type DropOptions, defaultOption, optionGroups, readOptions } from '../voicemail/drops.ts';
import { VoicemailDropStyles } from '../voicemail/DropStyles.tsx';
import { checkDropAgain, dismissDrop, sendDrop } from './dropCommand.ts';
import {
  type DroppingCard as DroppingCardModel, NO_TRANSITION, dropButtonId, dropDockCalls, dropEligibility, dropLineReleased, dropTransitionReducer, effectiveDrop, isDropping, nextExpiry, useLocalDrops,
} from './voicemailDrop.ts';
import { partyName } from './callView.ts';

// ---- The call's drop options ---------------------------------------------------------------------------

export type OptionsView =
  | { status: 'off' }
  | { status: 'loading' }
  | { status: 'ready'; value: DropOptions }
  | { status: 'error'; kind: 'missing' | 'denied' | 'error'; text: string; requestId: string | null };

/** Why the options couldn't be read, in the dock's words. */
export function optionsFault(error: unknown): Extract<OptionsView, { status: 'error' }> {
  const e = isApiRequestError(error) ? error : null;
  const requestId = e?.requestId ?? null;
  if (e && (e.status === 404 || e.status === 405 || e.status === 501 || (e.status === 503 && /schema_unavailable$/.test(e.code)))) {
    return { status: 'error', kind: 'missing', text: 'Voicemail drops aren’t available on this server yet.', requestId };
  }
  if (e?.status === 403) return { status: 'error', kind: 'denied', text: 'Your role can’t drop voicemail on this call.', requestId };
  return { status: 'error', kind: 'error', text: e?.status === 0 ? 'Couldn’t reach the server for your voicemail drops.' : 'Couldn’t load your voicemail drops.', requestId };
}

/** Only answered-or-answering outbound calls to outside numbers have options worth reading. */
const wantsOptions = (call: Call) => call.direction === 'outbound' && call.destination.kind === 'pstn';

function useDropOptions(client: Pick<VoicemailDropsClient, 'options'>, call: Call): [OptionsView, () => void] {
  const wanted = wantsOptions(call);
  const [view, setView] = useState<OptionsView>(wanted ? { status: 'loading' } : { status: 'off' });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!wanted) { setView({ status: 'off' }); return; }
    const abort = new AbortController();
    setView(v => (v.status === 'ready' ? v : { status: 'loading' }));
    client.options(call.id, abort.signal).then(
      body => {
        const value = readOptions(body);
        setView(value ? { status: 'ready', value } : { status: 'error', kind: 'error', text: 'The server sent voicemail drops this version can’t read.', requestId: null });
      },
      error => { if (!isAbortError(error)) setView(optionsFault(error)); },
    );
    return () => abort.abort();
  }, [client, call.id, call.from.number_id, call.state === 'active', wanted, tick]);
  return [view, () => setTick(t => t + 1)];
}

// ---- The control ---------------------------------------------------------------------------------------

/** The plain line under a control that can't be used yet for a reason of the library, not the call. */
function libraryReason(view: OptionsView, choice: DropOption | null): string | null {
  if (view.status === 'loading') return 'Loading your voicemail drops…';
  if (view.status === 'error') return view.text;
  if (view.status === 'ready' && !choice) return 'No voicemail drops for this number or your direct line yet.';
  return null;
}

export function DropVoicemail({ call, guard }: { call: Call; guard: ActivationGuard }) {
  const ws = useWorkspace();
  const { announce } = useAnnouncer();
  const uid = useId().replace(/:/g, '');
  const client = useMemo(() => new VoicemailDropsClient(ws.api), [ws.api]);
  const [view, reload] = useDropOptions(client, call);
  const locals = useLocalDrops();
  const local = locals.get(call.id) ?? null;
  const [open, setOpen] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const options = view.status === 'ready' ? view.value : null;
  const choice = defaultOption(options);
  const eligibility = dropEligibility(call, { local, pending: ws.callControl.pending(call), serverReason: options?.reasonCode ?? null });
  const callReason = eligibility.enabled ? null : eligibility.reason;
  const reason = callReason ?? libraryReason(view, choice);
  const canDrop = !reason && !!choice;
  const canPick = eligibility.enabled && !!options && options.options.length > 0;
  guard.observe(`drop:${call.id}`, `${canDrop}:${choice?.id ?? ''}`, Date.now());
  useEffect(() => { if (!canPick) setOpen(false); }, [canPick]);
  useEffect(() => { if (open) pickerRef.current?.querySelector<HTMLButtonElement>('.vmd-option')?.focus(); }, [open]);

  const drop = async (option: DropOption) => {
    if (!eligibility.enabled || !guard.allow(`drop:${call.id}`, Date.now())) return;
    setOpen(false);
    // Nothing this microphone hears should reach their voicemail while the line is being released.
    const leg = ws.callControl.engineCall(call);
    const mutedHere = !!leg && !leg.muted && leg.microphone !== 'none';
    if (mutedHere) ws.callControl.setMuted(call, true);
    announce(`Requesting ${option.name}. Waiting for your line’s release to be confirmed.`);
    const result = await sendDrop({ client, userId: ws.userId, getCall: id => ws.calls.get(id), upsert: ws.upsert, refetchCall: ws.refetchCall }, call, option);
    if (result.phase === 'failed') {
      // A failure can follow release. Never reopen the microphone based on a command failure.
      announce(`${result.error ?? 'Voicemail drop failed.'} Check your microphone before speaking.`, 'assertive');
    }
  };
  const closePicker = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    setOpen(false);
    moreRef.current?.focus();
  };

  const sending = local?.phase === 'sending';
  const numberLabel = call.from.number_id ? ws.numbers.value?.find(n => n.id === call.from.number_id)?.label ?? null : null;
  const what = sending ? local.name : choice ? `${choice.name}, ${formatDuration(choice.duration_ms)}` : null;
  const reasonId = `${uid}-why`;
  return (
    <div className="vmd-drop" role="group" aria-label="Voicemail drop">
      <VoicemailDropStyles />
      <div className="vmd-split">
        <button
          id={dropButtonId(call.id)}
          type="button"
          className="btn vmd-drop-main"
          onClick={() => { if (choice) void drop(choice); }}
          disabled={!canDrop}
          aria-busy={sending || undefined}
          aria-describedby={reasonId}
          aria-keyshortcuts="Alt+Shift+V"
        >
          <Icon icon={Voicemail} />
          <span className="vmd-drop-label">
            <span>{sending ? 'Requesting voicemail drop…' : 'Drop voicemail'}</span>
            {what ? <span className="vmd-drop-what">{what}</span> : null}
          </span>
        </button>
        <button
          ref={moreRef}
          type="button"
          className="btn vmd-drop-more"
          aria-expanded={open}
          aria-controls={`${uid}-picker`}
          aria-label="Choose another voicemail drop"
          title="Choose another voicemail drop"
          disabled={!canPick || sending}
          onClick={() => { setOpen(o => !o); if (!open) reload(); }}
        >
          <Icon icon={open ? ChevronUp : ChevronDown} />
        </button>
      </div>
      {reason ? (
        <p id={reasonId} className="inline-reason">
          {reason}
          {view.status === 'error' && view.kind === 'error' && !callReason ? <> <button type="button" className="btn btn-small btn-quiet" onClick={reload}>Try again</button></> : null}
          {view.status === 'ready' && !choice && !callReason ? <> <Link to="/settings">Add drops in Settings</Link></> : null}
        </p>
      ) : (
        <p id={reasonId} className="field-hint">Drop it after the beep: nothing listens for one. We’ll confirm when your line is released; the message can then play on its own.</p>
      )}
      {open && options ? (
        <div id={`${uid}-picker`} ref={pickerRef} className="vmd-picker" onKeyDown={closePicker}>
          {optionGroups(options.options, numberLabel).map(group => (
            <fieldset key={group.scope} className="vmd-group">
              <legend className="vmd-group-title">{group.title}</legend>
              {group.options.map(option => (
                <button key={option.id} type="button" className="vmd-option" onClick={() => void drop(option)} aria-label={`Drop ${option.name}, ${formatDuration(option.duration_ms)}${option.id === choice?.id ? ', the default' : ''}`}>
                  <span className="vmd-option-name">{option.name}</span>
                  <span className="vmd-option-meta">
                    {option.id === choice?.id ? <span className="row-badge vmd-default"><Icon icon={Star} size="1em" /> Default</span> : null}
                    <span className="num">{formatDuration(option.duration_ms)}</span>
                  </span>
                </button>
              ))}
            </fieldset>
          ))}
          {options.skipped > 0 ? <p className="meta">{options.skipped} drop{options.skipped === 1 ? '' : 's'} couldn’t be shown in this version.</p> : null}
          <p className="meta"><Link to="/settings">Manage my drops</Link></p>
        </div>
      ) : null}
      <DropCommandNotice call={call} />
    </div>
  );
}

/** Command outcome and connectivity are independent, including failures after agent release. Kept in the
 * dock after the active card leaves so a late command failure does not disappear with that card. */
export function DropCommandNotice({ call }: { call: Call }) {
  const ws = useWorkspace();
  const local = useLocalDrops().get(call.id);
  const client = useMemo(() => new VoicemailDropsClient(ws.api), [ws.api]);
  const released = dropLineReleased(call, local, ws.userId, ws.bridge.calls);
  const pending = local?.phase === 'sending' || local?.phase === 'queued' || local?.phase === 'releasing' || local?.phase === 'started';
  if (!local && call.state !== 'dropping') return null;
  if (released && local?.phase !== 'failed' && local?.phase !== 'unknown') return null;
  const waitingForAnswer = local?.phase === 'queued' && (call.state === 'created' || call.state === 'ringing');
  const text = waitingForAnswer ? 'Voicemail selected. Waiting for the call to answer before playing it.' : local?.phase === 'queued' ? 'Voicemail drop queued.'
    : pending || call.state === 'dropping' && !local ? 'Releasing your line for the voicemail drop.' : null;
  return (
    <p className={`cmd-status cmd-${local?.phase === 'failed' ? 'failed' : 'unknown'}`} role={local?.phase === 'failed' ? 'alert' : 'status'}>
      {local?.error ?? text}{' '}
      {waitingForAnswer ? 'You can cancel the call at any time.' : released ? 'Your line is released. Playback completion is separate.' : 'Your line’s release is not confirmed. Check the phone’s audio status before speaking or starting another call.'}
      {local?.requestId ? <span className="ref"> Reference <span className="mono">{local.requestId}</span></span> : null}
      {local?.phase === 'failed' ? <button type="button" className="btn btn-small btn-quiet" onClick={() => dismissDrop(call.id)}>Dismiss</button> : null}
      {local?.phase === 'unknown' ? <button type="button" className="btn btn-small" onClick={() => void checkDropAgain({ client, userId: ws.userId, getCall: id => ws.calls.get(id), upsert: ws.upsert, refetchCall: ws.refetchCall }, call.id)}>Check again</button> : null}
    </p>
  );
}

// ---- The one-second card --------------------------------------------------------------------------------

/**
 * Drops released while this dock is open get the cosmetic card: this tab's own, or a call the dock had on
 * screen (dropped from another tab or by the shortcut). A drop already under way when the page loads gets none.
 */
export function useDroppingCards(): DroppingCardModel[] {
  const ws = useWorkspace();
  const locals = useLocalDrops();
  const [state, dispatch] = useReducer(dropTransitionReducer, NO_TRANSITION);
  const onScreen = useRef(new Set<string>());
  useEffect(() => {
    const now = Date.now();
    for (const call of ws.calls.values()) {
      const local = locals.get(call.id) ?? null;
      if (!isDropping(call, local, ws.userId, ws.bridge.calls)) continue;
      if ((!local || local.userId !== ws.userId) && !onScreen.current.has(call.id)) continue;
      const drop = effectiveDrop(call, local);
      dispatch({ type: 'started', callId: call.id, name: drop?.name ?? local?.name ?? 'your voicemail drop', durationMs: drop?.durationMs ?? local?.durationMs ?? null, at: now });
    }
    onScreen.current = new Set(dropDockCalls(ws.calls, ws.liveCalls, locals, ws.userId, ws.bridge.calls)
      .filter(c => !isDropping(c, locals.get(c.id), ws.userId, ws.bridge.calls)).map(c => c.id));
  }, [ws.calls, ws.liveCalls, ws.userId, ws.bridge.calls, locals]);
  useEffect(() => {
    const wait = nextExpiry(state, Date.now());
    if (wait === null) return;
    const timer = setTimeout(() => dispatch({ type: 'tick', now: Date.now() }), wait + 5);
    return () => clearTimeout(timer);
  }, [state]);
  return state.cards;
}

/** "Dropping <name>": the lamp in the ended state, because this person's line already is. */
export function DroppingCard({ card }: { card: DroppingCardModel }) {
  const ws = useWorkspace();
  const { announce } = useAnnouncer();
  const call = ws.calls.get(card.callId);
  const party = call ? partyName(call, ws.userId, ws.userName) : null;
  useEffect(() => { announce(`Dropping ${card.name}. Your line is free for the next call.`); }, [announce, card.name]);
  return <DroppingCardView card={card} party={party ?? (call?.destination.kind === 'pstn' ? formatPhone(call.destination.e164) : null)} />;
}

export function DroppingCardView({ card, party }: { card: DroppingCardModel; party: string | null }) {
  return (
    <section className="call-card state-ended vmd-dropping" aria-labelledby={`vmd-dropping-${card.callId}`}>
      <VoicemailDropStyles />
      <div className="call-head">
        <p className="call-state"><span className="dot dot-end" aria-hidden="true" />Your line is released</p>
      </div>
      <h2 id={`vmd-dropping-${card.callId}`} className="party-name">Dropping {card.name}</h2>
      <p className="party-meta">
        {party ? `To ${party}. ` : ''}
        {card.durationMs !== null ? `The ${formatDuration(card.durationMs)} message is handled separately. ` : 'Message playback is handled separately. '}
        The call’s page shows whether it completed.
      </p>
    </section>
  );
}
